"use client";

import { TooltipContent } from "@faire/slate/private/tooltip/TooltipContent";
import { TooltipRoot } from "@faire/slate/private/tooltip/TooltipRoot";
import { TooltipTrigger } from "@faire/slate/private/tooltip/TooltipTrigger";
import {
  TooltipPosition,
  TooltipAlignment,
  TooltipRole,
  ITooltipWebProps,
  ITooltipPropsConstraints,
} from "@faire/slate/private/tooltip/types";
import React from "react";

type IProps = {
  /**
   * The tooltip alignment in reference to the trigger element.
   *
   * @default "middle"
   */
  alignment?: TooltipAlignment;

  /**
   * The tooltip trigger element.
   */
  children: React.ReactNode;

  /**
   * The label to display in the tooltip.
   */
  label: string;

  /**
   * The tooltip position in reference to the trigger element.
   *
   * @default "top"
   */
  position?: TooltipPosition;

  /**
   * **web**
   * The aria-label for trigger element when the role is `additional-info`.
   */
  triggerAriaLabel?: string;

  /**
   * **web**
   * The tooltip role. It defines how the tooltip is announced to users of assistive technologies.
   *
   * - `label`: The tooltip acts as a label for the trigger element. This is the equivalent of using the `aria-label` attribute
   *    and is shown when the trigger element receives focus or is hovered over.
   *
   * - `description`: The tooltip acts as a description for the trigger element. This is the equivalent of using the `aria-description` attribute
   *    and is shown when the trigger element receives focus or is hovered over.
   *
   * - `additional-info`: The tooltip is an additional information unrelated to the trigger element.
   *    This is typically used with the info icon and is shown when the trigger element receives focus or is hovered over.
   */
  role: TooltipRole;

  /**
   * **web**
   * Whether to use a react portal to render the tooltip.
   *
   * @default true
   */
  portaled?: boolean;

  /**
   * **web**
   * The z-index of the tooltip.
   *
   * @default 5
   */
  zIndex?: number;

  // DO NOT ADD any new prop without aligning with Slate team first
} & ITooltipWebProps;

/**
 * Tooltips display informative text when users hover or focus on an element.
 *
 * Key differences from popovers:
 * - Tooltips open/close based on focus or hover automatically
 * - Tooltips can only contain non-interactive text content
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * @trackcomponent
 * @version 2.0.0
 */
export const Tooltip: React.FC<ITooltipPropsConstraints<IProps>> = ({
  triggerAriaLabel,
  alignment = "middle",
  children,
  label,
  position = "top",
  role,
  portaled = true,
  zIndex = 5,
}) => {
  const needsArtificalInteraction = role === "additional-info";

  return (
    <TooltipRoot position={position} alignment={alignment} role={role}>
      <TooltipTrigger
        aria-label={triggerAriaLabel}
        addInteraction={needsArtificalInteraction}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent portaled={portaled} zIndex={zIndex}>
        <span>{label}</span>
      </TooltipContent>
    </TooltipRoot>
  );
};
